import { LocaleLink } from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, Check, ChevronDown, Shield, Zap, BadgeCheck, MapPin, TrendingUp, Target, Eye, Scale } from "lucide-react";
import { useState } from "react";
import { formatPrice } from "@/lib/currency";

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group min-h-[44px]"
        aria-expanded={open}
      >
        <span className="text-body font-medium pr-4">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <p className="text-caption leading-relaxed pb-5 pr-8">{a}</p>}
    </div>
  );
};

const CraftPricing = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const faqs = [
    { q: t("pricing.faq1q"), a: t("pricing.faq1a") },
    { q: t("pricing.faq2q"), a: t("pricing.faq2a") },
    { q: t("pricing.faq3q"), a: t("pricing.faq3a") },
    { q: t("pricing.faq4q"), a: t("pricing.faq4a") },
    { q: t("pricing.faq5q"), a: t("pricing.faq5a") },
  ];

  const { data: plans = [] } = useQuery({
    queryKey: ["craft-plans-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("craft_plans")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: addons = [] } = useQuery({
    queryKey: ["craft-addons-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("craft_plan_addons")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t("pricing.seoTitle")}
        description={t("pricing.seoDesc")}
        canonical="/craft/pricing"
      />
      <JsonLd
        includeService
        includeFaq
        breadcrumbs={[
          { name: "CRAFTFINDUS", url: "https://www.craftfindus.com/" },
          { name: t("pricing.seoTitle"), url: "https://www.craftfindus.com/de/craft/pricing" },
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 md:pb-16 section-padding text-center">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="text-micro tracking-[0.15em]">{t("pricing.heroLabel")}</span>
          <h1 className="text-display">
            {t("pricing.heroTitle1")}<br />
            <span className="text-accent">{t("pricing.heroTitle2")}</span>
          </h1>
          <p className="text-caption text-lg max-w-xl mx-auto leading-relaxed">
            {t("pricing.heroDesc")}
          </p>
        </div>
        {/* Trust bar */}
        <div className="max-w-3xl mx-auto mt-10 flex flex-wrap justify-center gap-6 md:gap-10">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-accent" /> {t("pricing.trustBarQualified")}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-accent" /> {t("pricing.trustBarVerified")}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-accent" /> {t("pricing.trustBarFair")}
          </span>
        </div>
      </section>

      {/* Positioning */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-headline mb-6">{t("pricing.posTitle")}</h2>
          <div className="space-y-4 text-caption leading-relaxed">
            <p>{t("pricing.posP1")}</p>
            <p className="text-body font-medium">{t("pricing.posP2")}</p>
            <p>{t("pricing.posP3")}</p>
            <p>{t("pricing.posP4")}</p>
          </div>
        </div>
      </section>

      {/* Billing Models */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-micro tracking-[0.15em]">{t("pricing.modelsLabel")}</span>
            <h2 className="text-headline mt-4">{t("pricing.modelsTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* FLEX */}
            <div className="p-8 border border-border bg-card hover:border-accent/30 transition-colors flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-accent" />
                <h3 className="text-subheadline">Flex</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">{t("pricing.flexSub")}</p>
              <p className="text-caption mb-6">{t("pricing.flexDesc")}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  t("pricing.flexF1"),
                  t("pricing.flexF2"),
                  t("pricing.flexF3"),
                  t("pricing.flexF4"),
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <LocaleLink to="/registrieren">
                <Button variant="quiet" size="lg" className="w-full group">
                  {t("pricing.flexCta")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
            </div>

            {/* PROFESSIONAL */}
            <div className="p-8 border border-accent bg-card shadow-[0_0_60px_hsl(var(--accent)/0.08)] flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-accent-foreground text-xs font-medium tracking-wide uppercase">
                {t("pricing.mostPopular")}
              </div>
              <div className="flex items-center gap-3 mb-2">
                <BadgeCheck className="w-5 h-5 text-accent" />
                <h3 className="text-subheadline">Professional</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">{t("pricing.proSub")}</p>
              <p className="text-caption mb-6">{t("pricing.proDesc")}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  t("pricing.proF1"),
                  t("pricing.proF2"),
                  t("pricing.proF3"),
                  t("pricing.proF4"),
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <LocaleLink to="/registrieren">
                <Button variant="commit" size="lg" className="w-full group">
                  {t("pricing.proCta")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
            </div>

            {/* PREMIUM */}
            <div className="p-8 border border-border bg-card hover:border-accent/30 transition-colors flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <Eye className="w-5 h-5 text-accent" />
                <h3 className="text-subheadline">Premium</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">{t("pricing.premSub")}</p>
              <p className="text-caption mb-6">{t("pricing.premDesc")}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  t("pricing.premF1"),
                  t("pricing.premF2"),
                  t("pricing.premF3"),
                  t("pricing.premF4"),
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <LocaleLink to="/registrieren">
                <Button variant="quiet" size="lg" className="w-full group">
                  {t("pricing.premCta")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-micro tracking-[0.15em]">{t("pricing.roiLabel")}</span>
            <h2 className="text-headline mt-4">{t("pricing.roiTitle")}</h2>
          </div>
          <div className="p-8 border border-accent/30 bg-accent/[0.03]">
            <p className="text-caption leading-relaxed text-center mb-6">
              {t("pricing.roiText")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
              <div>
                <p className="text-4xl font-medium text-accent" style={{ fontFamily: "'Cormorant Garamond', serif" }}>4.000+ €</p>
                <p className="text-caption mt-2">{t("pricing.roiAvgProject")}</p>
              </div>
              <div>
                <p className="text-4xl font-medium text-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>1–2</p>
                <p className="text-caption mt-2">{t("pricing.roiProjectsNeeded")}</p>
              </div>
            </div>
            <p className="text-center text-body font-medium mt-6 pt-6 border-t border-accent/20">
              {t("pricing.roiResult")}
            </p>
          </div>
        </div>
      </section>

      {/* Lead Quality Section */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Target className="w-5 h-5 text-accent" />
            <span className="text-micro tracking-[0.15em]">{t("pricing.qualityLabel")}</span>
          </div>
          <h2 className="text-headline mt-4 mb-4">{t("pricing.qualityTitle")}</h2>
          <p className="text-caption max-w-xl mx-auto mb-10">
            {t("pricing.qualityDesc")}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-left">
            {[
              { label: t("pricing.qPhone"), points: "+20" },
              { label: t("pricing.qBudget"), points: "+25" },
              { label: t("pricing.qImages"), points: "+15" },
              { label: t("pricing.qDesc"), points: "+10" },
              { label: t("pricing.qUrgency"), points: "+10" },
              { label: t("pricing.qContact"), points: "+10" },
            ].map((item, i) => (
              <div key={i} className="p-4 border border-border bg-card">
                <span className="text-accent font-semibold text-lg">{item.points}</span>
                <p className="text-xs text-muted-foreground mt-1">{item.label}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            {t("pricing.qualityMin")}
          </p>
        </div>
      </section>

      {/* Abo Plan Cards from DB */}
      {plans.length > 0 && (
        <section className="section-padding py-16 md:py-20 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-micro tracking-[0.15em]">{t("pricing.plansLabel")}</span>
              <h2 className="text-headline mt-4">{t("pricing.plansTitle")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...plans].reverse().map((plan: any) => {
                const isPro = plan.key === "pro";
                const features = Array.isArray(plan.features) ? plan.features : [];
                return (
                  <div
                    key={plan.id}
                    className={`relative p-6 md:p-8 border bg-card flex flex-col transition-all duration-300 ${
                      isPro
                        ? "border-accent shadow-[0_0_60px_hsl(var(--accent)/0.12)] md:-mt-4 md:mb-0"
                        : "border-border hover:border-accent/30"
                    }`}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-accent-foreground text-xs font-medium tracking-wide uppercase">
                        {t("pricing.mostPopular")}
                      </div>
                    )}
                    <div className="space-y-4 flex-1">
                      <h3 className="text-subheadline">{plan.name}</h3>
                      <div>
                        <span className="price-display text-4xl">
                          {formatPrice(plan.monthly_price_cents, lang)}
                        </span>
                        <span className="text-caption ml-1">{t("common.perMonth")}</span>
                      </div>
                      <p className="text-caption">
                        {plan.included_accepted_leads_per_month} {t("pricing.leadsIncluded")}
                      </p>
                      <div className="divider-line" />
                      <ul className="space-y-3">
                        {features.map((f: string, fi: number) => (
                          <li key={fi} className="flex items-start gap-3 text-sm">
                            <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-8">
                      <LocaleLink to="/registrieren">
                        <Button
                          variant={isPro ? "commit" : "quiet"}
                          size="lg"
                          className="w-full group"
                        >
                          {t("pricing.choosePlan")}
                          <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                        </Button>
                      </LocaleLink>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Add-ons */}
      {addons.filter((a: any) => !a.key.startsWith("radius_")).length > 0 && (
        <section className="section-padding py-16 md:py-20 border-t border-border">
          <div className="max-w-3xl mx-auto">
            <span className="text-micro tracking-[0.15em]">{t("pricing.addonsLabel")}</span>
            <h2 className="text-headline mt-4 mb-8">{t("pricing.addonsTitle")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {addons.filter((a: any) => !a.key.startsWith("radius_")).map((addon: any) => (
                <div key={addon.id} className="p-6 border border-border bg-card hover:border-accent/30 transition-colors">
                  <h3 className="text-body font-medium">{addon.name}</h3>
                  <p className="price-display text-2xl mt-2">
                    {formatPrice(addon.price_cents, lang)}
                  </p>
                  <p className="text-caption mt-1">{t("common.perMonth")}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Radius Add-ons */}
      {addons.filter((a: any) => a.key.startsWith("radius_")).length > 0 && (
        <section className="section-padding py-16 md:py-20 border-t border-border">
          <div className="max-w-3xl mx-auto">
            <span className="text-micro tracking-[0.15em]">{t("pricing.radiusLabel")}</span>
            <h2 className="text-headline mt-4 mb-4">{t("pricing.radiusTitle")}</h2>
            <p className="text-caption mb-8 max-w-xl leading-relaxed">{t("pricing.radiusDesc")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {addons.filter((a: any) => a.key.startsWith("radius_")).map((addon: any) => {
                const km = addon.key.replace("radius_", "").replace("km", "");
                return (
                  <div key={addon.id} className="p-6 border border-border bg-card hover:border-accent/30 transition-colors text-center">
                    <MapPin className="w-5 h-5 text-accent mx-auto mb-3" />
                    <h3 className="text-subheadline">{km} km</h3>
                    <p className="price-display text-2xl mt-2">
                      {formatPrice(addon.price_cents, lang)}
                    </p>
                    <p className="text-caption mt-1">{t("common.perMonth")}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Trust Badges */}
      <section className="section-padding py-12 border-t border-border">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-8">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-4 h-4 text-accent" /> {t("pricing.trustNoContract")}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Zap className="w-4 h-4 text-accent" /> {t("pricing.trustInstant")}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <BadgeCheck className="w-4 h-4 text-accent" /> {t("pricing.trustVerified")}
          </span>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <span className="text-micro tracking-[0.15em]">{t("pricing.faqLabel")}</span>
          <h2 className="text-headline mt-4 mb-10">{t("pricing.faqTitle")}</h2>
          <div>
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding py-20 border-t border-border text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-headline">{t("pricing.ctaTitle")}</h2>
          <p className="text-caption leading-relaxed">
            {t("pricing.ctaDesc")}
          </p>
          <LocaleLink to="/registrieren">
            <Button variant="commit" size="xl" className="group mt-4">
              {t("pricing.ctaBtn")}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </LocaleLink>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CraftPricing;
